import type { ActorEvent } from '@lighthouse/core'
import { EVENT_TYPE, type OnOffBlockAbstract, SwitchTypeEnum } from '@lighthouse/core'
import {
    applyFillPickerValue4CssColorValue,
    checkBoxShapeMap,
    flexMinSizeCSS,
    getWithScopeIdAndRecordIdAndColumnId,
    InputCheckBox,
    InputSwitch,
    useActionRunningLoadings,
    useFillPickerContext
} from '@lighthouse/shared'
import React, { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useContainerBlockContext, useCustomViewBlockContext, useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

interface OnOffBlockProps {
    blockData: OnOffBlockAbstract
    onTriggerEvent?: (id: string, actorEvent: ActorEvent) => Promise<void>
    onValueChange?: (checked: boolean) => void
}

const SCxContainer = styled(AnimationDecorators)<{ loading?: boolean }>`
    ${flexMinSizeCSS}
`

const OnOffBlock: React.FunctionComponent<OnOffBlockProps> = ({ blockData, onTriggerEvent, onValueChange }) => {
    const { id, title, config } = blockData
    const { defaultChecked, showType, shape, eventList, unCheckedIcon, checkedIcon, breakPoint, anchor } = config
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const { palettes } = useFillPickerContext()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, scope, record?.id])
    const changeEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.CHANGE), [eventList])
    const handleChange = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            onValueChange?.(ev.currentTarget.checked)
            if (changeEvent) {
                handleActionTriggerWithLoading?.({ id: uniqueId, actorEvent: changeEvent, trigger: onTriggerEvent })
            }
        },
        [changeEvent, handleActionTriggerWithLoading, onTriggerEvent, onValueChange, uniqueId]
    )

    const handleClick = useCallback(
        (ev: React.MouseEvent<HTMLDivElement>) => {
            if (changeEvent) {
                ev.stopPropagation()
            }
        },
        [changeEvent]
    )

    const content = useMemo(() => {
        if (showType === SwitchTypeEnum.CHECKBOX) {
            const isCheckbox = !shape || shape === 'checkbox'
            const checkBoxShape = shape ? checkBoxShapeMap[shape] : checkBoxShapeMap.checkbox
            return (
                <InputCheckBox
                    isCheckbox={isCheckbox}
                    progressed={loadings[uniqueId]}
                    size="100%"
                    defaultBgcolor={isCheckbox ? 'var(--color-white)' : 'none'}
                    defaultChecked={defaultChecked}
                    unCheckIcon={checkBoxShape?.[0]}
                    unCheckIconColor="var(--color-app-main)"
                    checkIcon={checkBoxShape?.[1]}
                    checkIconColor="var(--color-app-main)"
                    onChange={handleChange}
                />
            )
        }

        if (showType === SwitchTypeEnum.SWITCH) {
            return (
                <InputSwitch
                    progressed={loadings[id]}
                    defaultChecked={defaultChecked}
                    color="var(--color-app-main)"
                    onChange={handleChange}
                />
            )
        }

        const checkIconColor = applyFillPickerValue4CssColorValue(checkedIcon?.color?.color, palettes) || 'var(--color-app-main)'
        const unCheckIconColor = applyFillPickerValue4CssColorValue(unCheckedIcon?.color?.color, palettes) || 'var(--color-gray-400)'
        return (
            <InputCheckBox
                size="100%"
                defaultBgcolor="transparent"
                progressed={loadings[id]}
                defaultChecked={defaultChecked}
                unCheckIcon={unCheckedIcon?.icon?.value || 'SmileCircleIconLibrary'}
                unCheckIconColor={unCheckIconColor}
                checkIcon={checkedIcon?.icon?.value || 'SmileCircleIconLibrary'}
                checkIconColor={checkIconColor}
                onChange={handleChange}
            />
        )
    }, [
        checkedIcon?.color?.color,
        checkedIcon?.icon?.value,
        defaultChecked,
        handleChange,
        id,
        loadings,
        palettes,
        shape,
        showType,
        unCheckedIcon?.color?.color,
        unCheckedIcon?.icon?.value,
        uniqueId
    ])

    return (
        <SCxContainer animation={breakPoint.animation} blockId={id} blockName={title} id={effectiveAnchorId} onClick={handleClick}>
            {content}
        </SCxContainer>
    )
}

export default memo(OnOffBlock)
